import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import OnboardingChatPanel from './OnboardingChatPanel';
import ChatFloatingButton from './ChatFloatingButton';
import StepProgressIndicator from './StepProgressIndicator';
import { useOnboarding } from '../../context/OnboardingContext';
import { subscribeCmsLoading, getCmsLoadingState, prefetchCmsKeys } from '../../hooks/useCMS';
import { SCREEN_SEQUENCE, findScreenIndex } from '../../config/screenCmsKeys';
import '../../styles/onboarding.css';

/**
 * OnboardingLayout
 * Split-screen wrapper used by every onboarding screen.
 */

const OnboardingLayout = ({
  children,
  chatHeader,
  preloadedQuestions,
  rightPanelContent,
  fullBleed,
  bgVariant,
  currentSection,
  rightPanelMode,
  extraLoading,
  showHeader,
  leftPanelTransparent,
}) => {
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
  // Initialise from current state so that if all CMS is pre-cached the
  // component starts with cmsLoading=false and never shows a spinner at all.
  const [cmsLoading, setCmsLoading] = useState(() => getCmsLoadingState());
  const { chatHistory, sendChatMessage } = useOnboarding();
  const location = useLocation();
  const dispatch = useDispatch();

  // Track CMS loading state — subscribeCmsLoading fires immediately with current state.
  useEffect(() => subscribeCmsLoading(setCmsLoading), []);

  // Prefetch the next screen's CMS keys silently while the user is on this
  // screen, so there is no loader when they navigate forward.
  useEffect(() => {
    const idx = findScreenIndex(location.pathname);
    if (idx === -1 || idx >= SCREEN_SEQUENCE.length - 1) return;
    prefetchCmsKeys(dispatch, SCREEN_SEQUENCE[idx + 1].keys);
  }, [location.pathname]);

  const toggleMobileChat = () => setIsMobileChatOpen((prev) => !prev);

  // Show content as soon as CMS is ready — no artificial animation delay.
  const showContent = !cmsLoading && !extraLoading;

  // ── Full-bleed mode (transition screens) ────────────────────────────────
  if (fullBleed) {
    return (
      <div
        className={`hg-onboarding-root hg-onboarding-root--fullbleed hg-step-transition hg-onboarding-root--${bgVariant || 'gradient'}`}
        data-testid="onboarding-layout-fullbleed"
      >
        {showContent ? children : <div className="hg-left-panel-loader"><div className="hg-nav-spinner" /></div>}
        {rightPanelMode !== 'none' && (
          <ChatFloatingButton onClick={toggleMobileChat} isOpen={isMobileChatOpen} />
        )}
      </div>
    );
  }

  // ── Standard mode (left content panel + right panel) ────────────────────
  const rightPanelClass = `hg-right-panel${isMobileChatOpen ? ' hg-right-panel--open' : ''}`;

  return (
    <div className="hg-onboarding-root" data-testid="onboarding-layout-standard">
      <div className="hg-onboarding-body">
        <div className={`hg-left-panel hg-step-transition hg-ob-scroll${leftPanelTransparent ? ' hg-left-panel--transparent' : ''}`}>
          {currentSection && (
            <div className="hg-progress-indicator-wrapper">
              <StepProgressIndicator currentSection={currentSection} />
            </div>
          )}
          {showContent ? children : <div className="hg-left-panel-loader hg-left-panel-loader--subtle"><div className="hg-nav-spinner" /></div>}
        </div>

        {rightPanelMode !== 'none' && (
          <div className={rightPanelClass}>
            {rightPanelContent || (rightPanelMode === 'chat' && (
              <OnboardingChatPanel
                chatHistory={chatHistory}
                onSendMessage={sendChatMessage}
                preloadedQuestions={preloadedQuestions}
                header={chatHeader}
              />
            ))}
          </div>
        )}
      </div>

      {rightPanelMode !== 'none' && (
        <ChatFloatingButton onClick={toggleMobileChat} isOpen={isMobileChatOpen} />
      )}
    </div>
  );
};

OnboardingLayout.propTypes = {
  children:            PropTypes.node.isRequired,
  chatHeader:          PropTypes.node,
  preloadedQuestions:  PropTypes.arrayOf(PropTypes.string),
  rightPanelContent:   PropTypes.node,
  fullBleed:           PropTypes.bool,
  bgVariant:           PropTypes.oneOf(['gradient', 'seafoam', 'hotpink', 'skyblue', 'gold']),
  currentSection:      PropTypes.oneOf(['basics', 'guidebook', 'messaging']),
  rightPanelMode:      PropTypes.oneOf(['chat', 'phone', 'none']),
  extraLoading:           PropTypes.bool,
  showHeader:             PropTypes.bool,
  leftPanelTransparent:   PropTypes.bool,
};

OnboardingLayout.defaultProps = {
  chatHeader:          null,
  preloadedQuestions:  [],
  rightPanelContent:   null,
  fullBleed:           false,
  bgVariant:           'gradient',
  currentSection:      undefined,
  rightPanelMode:      'chat',
  extraLoading:           false,
  showHeader:             false,
  leftPanelTransparent:   false,
};

export default OnboardingLayout;
